import { Drawer, Title } from "@mantine/core"
import { PaginationState } from "@tanstack/react-table"
import { AreYouSureModal } from "@tfg/ui"
import { AxiosError } from "axios"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { CountryIso3166 } from "@tfg/types/constants/country.constants"

import { queryClient } from "~/backend/queryClient"
import type { CreateProductionSiteResponseDto } from "~/gen/api"
import {
  findProductionSitesForSupplierQueryKey,
  FindProductionSitesForSupplierResponseDto,
  useCreateProductionSite,
  useDeleteProductionSite,
} from "~/gen/api"
import { useUpdateProductionSite } from "~/gen/api/hooks/useUpdateProductionSite.ts"
import { useGetProductionSiteFormSchema } from "~/pages/SupplierPortal/ProductionSites/hooks/useProductionSiteFormSchema"
import {
  useGetProductionSiteFormTabs,
  type ProductionSiteFormValues,
} from "~/pages/SupplierPortal/ProductionSites/schemas/productionSiteForm.schema"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"
import { SchemaForm } from "~/utils/zod/schema-form"

/** Initial values for the form; countryIso3166 may be empty before user selection. */
type ProductionSiteInitialValues = Omit<
  ProductionSiteFormValues,
  "countryIso3166"
> & { countryIso3166: CountryIso3166 | null }

const defaultInitialValues: ProductionSiteInitialValues = {
  repositoryRecordId: undefined,
  name: "",
  parentCompany: "",
  internalCode: "",
  performedActivities: [],
  registrationIds: [],
  street: "",
  city: "",
  postalCode: "",
  longitude: undefined,
  latitude: undefined,
  countryIso3166: null,
}

interface BaseProps {
  readonly opened: boolean
  readonly onClose: () => void
}

interface CreateModeProps extends BaseProps {
  readonly productionSite?: undefined
  readonly pagination: PaginationState
  readonly onProductionSiteCreated?: (
    productionSiteId: string,
    kind: CreateProductionSiteResultKind | undefined
  ) => void
}

interface EditModeProps extends BaseProps {
  readonly productionSite: FindProductionSitesForSupplierResponseDto
  readonly pagination: PaginationState
  readonly onProductionSiteCreated?: undefined
}

type Props = CreateModeProps | EditModeProps

type CreateProductionSiteResultKind = CreateProductionSiteResponseDto["kind"]

type CreateProductionSiteMutationPayload = {
  readonly kind?: CreateProductionSiteResponseDto["kind"]
  readonly id?: string
  readonly productionSite?: {
    readonly id?: string
  }
}

const isCreateProductionSiteResultKind = (
  value: unknown
): value is CreateProductionSiteResultKind => {
  return value === "created" || value === "merged"
}

const parseCreateProductionSiteMutationPayload = (
  value: unknown
): CreateProductionSiteMutationPayload | null => {
  if (typeof value !== "object" || value === null) {
    return null
  }

  const kindRaw = Reflect.get(value, "kind")
  const kind = isCreateProductionSiteResultKind(kindRaw) ? kindRaw : undefined

  const productionSiteRaw = Reflect.get(value, "productionSite")
  const productionSiteIdRaw =
    typeof productionSiteRaw === "object" && productionSiteRaw !== null
      ? Reflect.get(productionSiteRaw, "id")
      : undefined

  const topLevelIdRaw = Reflect.get(value, "id")

  return {
    kind,
    id: typeof topLevelIdRaw === "string" ? topLevelIdRaw : undefined,
    productionSite:
      typeof productionSiteIdRaw === "string"
        ? { id: productionSiteIdRaw }
        : undefined,
  }
}

function isEditMode(props: Props): props is EditModeProps & {
  productionSite: FindProductionSitesForSupplierResponseDto
} {
  return props.productionSite !== undefined
}

export const EditProductionSiteDrawer = (props: Props) => {
  const { opened, onClose } = props
  const isEdit = isEditMode(props)
  const { t } = useTranslation(["supplier"])
  const productionSite = isEdit ? props.productionSite : undefined
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [, setDuplicateCandidateId] = useState<string | null>(null)
  const onDuplicateCandidateForCreateNew = useCallback(
    (duplicateCandidateId: string | null): void => {
      setDuplicateCandidateId(duplicateCandidateId)
    },
    []
  )

  const createMutation = useCreateProductionSite({
    mutation: {
      onSuccess: (data) => {
        const responsePayload = parseCreateProductionSiteMutationPayload(
          data.data
        )
        const productionSiteId =
          responsePayload?.productionSite?.id ?? responsePayload?.id
        const createSuccessTranslationTitle =
          responsePayload?.kind === "merged"
            ? t("productionSites.create.successMerged.title")
            : t("productionSites.create.successCreated.title")

        const createSuccessTranslationMessage =
          responsePayload?.kind === "merged"
            ? t("productionSites.create.successMerged.message")
            : t("productionSites.create.successCreated.message")

        onClose()
        showSuccessNotification({
          message: createSuccessTranslationMessage,
          title: createSuccessTranslationTitle,
        })
        ;(queryClient.invalidateQueries({
          queryKey: findProductionSitesForSupplierQueryKey({
            pagination: {
              take: props.pagination.pageSize,
              skip: props.pagination.pageIndex,
            },
          }),
        }),
          productionSiteId !== undefined
            ? props.onProductionSiteCreated?.(
                productionSiteId,
                responsePayload?.kind
              )
            : undefined)
      },
      onError: (error: AxiosError) => {
        showAxiosError({
          notificationTitle: t("productionSites.create.error"),
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
      },
    },
  })

  const { mutate: deleteProductionSite } = useDeleteProductionSite({
    mutation: {
      onSuccess: () => {
        showSuccessNotification({
          message: t("productionSites.delete.success.message"),
          title: t("productionSites.delete.success.title"),
        })
        queryClient.invalidateQueries({
          queryKey: findProductionSitesForSupplierQueryKey({
            pagination: {
              take: props.pagination.pageSize,
              skip: props.pagination.pageIndex,
            },
          }),
        })
        onClose()
      },
      onError: (error: AxiosError) => {
        showAxiosError({
          notificationTitle: t("productionSites.delete.error"),
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
        queryClient.invalidateQueries({
          queryKey: findProductionSitesForSupplierQueryKey({
            pagination: {
              take: props.pagination.pageSize,
              skip: props.pagination.pageIndex,
            },
          }),
        })
      },
    },
  })

  const updateMutation = useUpdateProductionSite({
    mutation: {
      onSuccess: () => {
        onClose()
        queryClient.invalidateQueries({
          queryKey: findProductionSitesForSupplierQueryKey({
            pagination: {
              take: props.pagination.pageSize,
              skip: props.pagination.pageIndex,
            },
          }),
        })
        showSuccessNotification({
          message: t("productionSites.update.success.message"),
          title: t("productionSites.update.success.title"),
        })
      },
      onError: (error: AxiosError) => {
        showAxiosError({
          notificationTitle: t("productionSites.update.error"),
          notificationMessage: error.message,
          showNotification: true,
          error,
        })
      },
    },
  })

  const {
    schema,
    isLoading: schemaLoading,
    businessRelationshipOptions,
  } = useGetProductionSiteFormSchema(isEdit, onDuplicateCandidateForCreateNew)

  const isLoading = isEdit ? updateMutation.isPending : createMutation.isPending

  const initialValues: Record<string, unknown> = isEdit
    ? {
        repositoryRecordId: undefined,
        name: productionSite?.name ?? "",
        parentCompany: productionSite?.parentCompany.name ?? "",
        internalCode: productionSite?.internalCode ?? "",
        performedActivities:
          productionSite?.performedActivities?.map((a) => a.id) ?? [],
        registrationIds:
          productionSite?.registrationIds?.map((r) => ({
            id: r.id,
            registrationId: r.registrationId,
            type: r.type,
          })) ?? [],
        street: productionSite?.street ?? "",
        city: productionSite?.city ?? "",
        postalCode: productionSite?.postalCode ?? "",
        longitude: productionSite?.longitude ?? undefined,
        latitude: productionSite?.latitude ?? undefined,
        countryIso3166: productionSite?.country ?? null,
      }
    : {
        ...defaultInitialValues,
        parentCompany: businessRelationshipOptions[0]?.value ?? "",
      }

  const handleSubmit = (values: ProductionSiteFormValues): void => {
    if (isEdit && productionSite) {
      updateMutation.mutate({
        productionSiteId: productionSite.id,
        data: {
          name: values.name,
          street: values.street,
          city: values.city,
          postalCode: values.postalCode,
          longitude: values.longitude,
          latitude: values.latitude,
          countryIso3166: values.countryIso3166,
          internalCode: values.internalCode,
          performedActivities: values.performedActivities,
          registrationIds: values.registrationIds,
        },
      })
    } else {
      createMutation.mutate({
        data: {
          ...(values.repositoryRecordId !== undefined
            ? { repositoryRecordId: values.repositoryRecordId }
            : {}),
          name: values.name,
          street: values.street,
          city: values.city,
          postalCode: values.postalCode,
          longitude: values.longitude,
          latitude: values.latitude,
          countryIso3166: values.countryIso3166,
          internalCode: values.internalCode ?? "",
          performedActivities: values.performedActivities,
          registrationIds: values.registrationIds ?? [],
          businessRelationshipId: values.parentCompany,
        },
      })
    }
  }

  const tabs = useGetProductionSiteFormTabs()
  const tabsWithTranslatedLabels = tabs.map((tab) => ({
    ...tab,
    label: (t as (key: string) => string)(tab.label),
  }))

  return (
    <Drawer
      opened={opened}
      position="right"
      onClose={onClose}
      title={
        <Title order={3}>
          {isEdit
            ? t("productionSiteModal.editTitle")
            : t("productionSiteModal.createTitle")}
        </Title>
      }
      styles={{
        body: {
          flex: "1 1 0%",
          minHeight: "0",
          overflow: "auto",
        },
        content: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
      size="xl"
      h="100%"
      bg="red"
    >
      <SchemaForm
        deleteLabel={t("productionSiteModal.actions.delete")}
        handleDelete={() => setIsDeleteModalOpen(true)}
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitLabel={t("productionSiteModal.actions.save")}
        cancelLabel={t("productionSiteModal.actions.cancel")}
        onCancel={onClose}
        isLoading={isLoading || schemaLoading}
        tabs={tabsWithTranslatedLabels}
        translateError={(error) =>
          t(
            error as
              | "productionSiteModal.errors.nameRequired"
              | "productionSiteModal.errors.countryMustBeValid"
          )
        }
      />
      <AreYouSureModal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          if (productionSite) {
            deleteProductionSite({ productionSiteId: productionSite.id })
          }
          setIsDeleteModalOpen(false)
        }}
        areYouSureText={t("productionSiteModal.delete.message")}
        confirmText={t("productionSiteModal.delete.confirm")}
        cancelText={t("productionSiteModal.delete.cancel")}
        title={t("productionSiteModal.delete.title")}
      />
    </Drawer>
  )
}
